import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ESTIMATING PAGE - ESTIMATIONS LISTING
 *
 * This page displays all estimations with search and filter capabilities.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { PageHeader, PageHeaderWithBack, useToast } from "@/components/common";
import { Modal } from "@/components/common/Modal/Modal";
import { Pagination } from "@/components/common/Pagination";
import { useAuth } from "@/context/AuthContext";
import { quotesService } from "@/api/services/quotes.service";
import { sitesService } from "@/api/services/sites.service";
import { useAlert } from "@/hooks/useAlert";
import { useCreateJobFromQuote } from "./hooks/useCreateJobFromQuote";
import { getQuoteStatusForDisplay, ADDITIONAL_WORKS_QUOTE_TYPE, } from "@/utils/quoteHelpers";
import { EstimatingTable, EstimatingFilterModal } from "./components";
import { formatSiteAddress } from "@/utils/address";
import styles from "./Estimating.module.css";
const STATUS_LABELS = {
    draft: "Requested",
    inprogress: "In Progress",
    pending: "Pending Approval",
    approved: "Approved",
    declined: "Declined",
    cancelled: "Cancelled",
};
export const Estimating = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAdmin } = useAuth();
    const { error: toastError, warning } = useToast();
    const { confirm } = useAlert();
    const [searchParams, setSearchParams] = useSearchParams();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(null);
    // Modal state (for Add Estimation)
    const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
    const [availableSites, setAvailableSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(false);
    const [selectedSiteId, setSelectedSiteId] = useState("");
    // Determine base path (admin or staff)
    const basePath = useMemo(() => {
        return location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    }, [location.pathname]);
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(10);
    // Filter state
    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
    const [quoteTypeFilter, setQuoteTypeFilter] = useState(searchParams.get("quoteType") || "");
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [filterModalType, setFilterModalType] = useState(null);
    // Fetch quotes from API
    const fetchQuotes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await quotesService.getQuotes({
                search: searchTerm || undefined,
                status: statusFilter !== "all" ? statusFilter : undefined,
                quoteType: quoteTypeFilter && quoteTypeFilter !== "all"
                    ? quoteTypeFilter
                    : undefined,
                page: currentPage,
                pageSize,
            });
            const { data, pagination } = response.data;
            // Map API response to EstimatingQuote format
            const mappedQuotes = data.map((quote) => ({
                id: quote.id,
                quoteId: quote.quoteNumber || quote.id,
                createdAt: quote.createdAt,
                quoteName: quote.title,
                siteName: quote.siteName || "-",
                // Always prefer quote.address (full concatenated address) over quote.siteAddress (site's street-only address)
                siteAddress: quote.address || quote.siteAddress || "-",
                suburb: quote.suburb || "-",
                clientName: quote.clientName || "-",
                status: getQuoteStatusForDisplay(quote.status),
                linkedJobId: quote.linkedJobId || null,
                quoteType: quote.quoteType ?? null,
                jobNumber: quote.jobNumber ?? null,
            }));
            setQuotes(mappedQuotes);
            setCurrentPage(pagination.pageNumber);
            setTotalPages(pagination.totalPages);
        }
        catch (err) {
            setError("Failed to load estimations");
            console.error("Error fetching quotes:", err);
        }
        finally {
            setLoading(false);
        }
    }, [searchTerm, statusFilter, quoteTypeFilter, currentPage, pageSize]);
    useEffect(() => {
        fetchQuotes();
    }, [fetchQuotes]);
    const buildSearchParams = useCallback((opts) => {
        const params = {};
        if (opts.search)
            params.search = opts.search;
        if (opts.status && opts.status !== "all")
            params.status = opts.status;
        if (opts.quoteType)
            params.quoteType = opts.quoteType;
        return params;
    }, []);
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            setSearchParams(buildSearchParams({
                search: searchTerm || undefined,
                status: statusFilter,
                quoteType: quoteTypeFilter || undefined,
            }));
            setCurrentPage(1);
        }
    };
    const handleFilterChange = (action) => {
        if (action === "all") {
            setStatusFilter("all");
            setQuoteTypeFilter("");
            setCurrentPage(1);
            setShowFilterMenu(false);
            setSearchParams(buildSearchParams({
                search: searchTerm || undefined,
                status: "all",
                quoteType: undefined,
            }));
            return;
        }
        setFilterModalType(action);
        setShowFilterMenu(false);
    };
    const handleFilterApply = (value) => {
        if (filterModalType === "status") {
            const status = (value || "all");
            setStatusFilter(status);
            setSearchParams(buildSearchParams({
                search: searchTerm || undefined,
                status,
                quoteType: quoteTypeFilter || undefined,
            }));
        }
        else if (filterModalType === "quoteType") {
            setQuoteTypeFilter(value || "");
            setSearchParams(buildSearchParams({
                search: searchTerm || undefined,
                status: statusFilter,
                quoteType: value || undefined,
            }));
        }
        setCurrentPage(1);
        setFilterModalType(null);
    };
    const getActiveFilterCount = () => {
        let count = 0;
        if (statusFilter !== "all")
            count++;
        if (quoteTypeFilter)
            count++;
        return count;
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleEditQuote = useCallback((quoteId, quoteType) => {
        if (quoteType === ADDITIONAL_WORKS_QUOTE_TYPE) {
            navigate(`${basePath}/estimating/${quoteId}/edit-additional`);
        }
        else {
            navigate(`${basePath}/estimating/${quoteId}/edit`);
        }
    }, [basePath, navigate]);
    const handleViewQuote = useCallback((quoteId) => {
        // Pass current location so back button can return here
        navigate(`${basePath}/estimating/${quoteId}/view`, {
            state: { from: location.pathname },
        });
    }, [basePath, navigate, location.pathname]);
    const handleDeleteQuote = useCallback(async (quoteId) => {
        const confirmed = await confirm("Are you sure you want to delete this estimation?");
        if (!confirmed)
            return;
        try {
            setIsDeleting(quoteId);
            await quotesService.deleteQuote(quoteId);
            await fetchQuotes();
        }
        catch (err) {
            console.error("Error deleting quote:", err);
            toastError("Failed to delete estimation");
        }
        finally {
            setIsDeleting(null);
        }
    }, [confirm, fetchQuotes, toastError]);
    // Create Job from Quote handler
    const { createJob, navigateToJob } = useCreateJobFromQuote();
    // Handle Edit Job - navigate to existing job
    const handleEditJob = useCallback((jobId) => {
        navigateToJob(jobId);
    }, [navigateToJob]);
    const handleCreateJob = useCallback(async (quoteId) => {
        try {
            // Fetch the full quote data
            // quoteId might be quoteNumber or actual ID, so try both
            let fullQuote;
            try {
                // First try to fetch by ID directly
                const response = await quotesService.getQuote(quoteId);
                fullQuote = response.data;
            }
            catch {
                // If that fails, search for it by quoteNumber
                const response = await quotesService.getQuotes({
                    search: quoteId,
                    pageSize: 100,
                });
                fullQuote = response.data.data.find((q) => q.quoteNumber === quoteId || q.id === quoteId);
            }
            if (!fullQuote) {
                console.error("Full quote data not found");
                return;
            }
            await createJob(fullQuote, { navigateToJob: true });
            // Navigation to job edit is handled by the hook; no need to refresh here
        }
        catch (err) {
            console.error("Error creating job from quote:", err);
        }
    }, [createJob, fetchQuotes]);
    // Fetch available sites when modal opens
    const fetchSites = useCallback(async () => {
        try {
            setSitesLoading(true);
            const response = await sitesService.getSites({ pageSize: 1000 });
            setAvailableSites(response.data.data);
        }
        catch (err) {
            console.error("Failed to fetch sites:", err);
            toastError("Failed to load sites");
        }
        finally {
            setSitesLoading(false);
        }
    }, [toastError]);
    // Open modal and fetch sites
    const handleNewEstimation = () => {
        setIsSiteModalOpen(true);
        setSelectedSiteId("");
        fetchSites();
    };
    // Handle site selection
    const handleSiteSelect = useCallback(async (siteId) => {
        if (!siteId)
            return;
        try {
            // Fetch the full site details to check for company
            const response = await sitesService.getSite(siteId);
            const site = response.data;
            // Check if the site has a company filled out
            if (!site.companyName) {
                warning("Please fill out the Company field for this site before creating an estimate.");
                return;
            }
            // Close modal and navigate to create estimate with siteId
            setIsSiteModalOpen(false);
            navigate(`${basePath}/estimating/create?siteId=${siteId}`);
        }
        catch (err) {
            console.error("Failed to fetch site details:", err);
            toastError("Failed to load site details");
        }
    }, [basePath, navigate, warning, toastError]);
    // Handle add new site + estimate
    const handleAddNewEstimate = () => {
        setIsSiteModalOpen(false);
        navigate(`${basePath}/estimating/create?newSite=true`);
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Estimations", onBack: () => navigate(-1), actions: _jsxs("div", { className: styles.actions, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsx("span", { className: styles.searchIcon, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }) }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search estimations...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyPress: handleSearch, autoFocus: true })] }), _jsxs("div", { className: styles.filterDropdown, children: [_jsxs("button", { className: styles.filterBtn, onClick: () => setShowFilterMenu(!showFilterMenu), title: "Filter estimations", children: [_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", children: _jsx("path", { d: "M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" }) }), getActiveFilterCount() > 0 && (_jsx("span", { className: styles.filterBadge, children: getActiveFilterCount() }))] }), showFilterMenu && (_jsxs("div", { className: styles.filterMenu, children: [(statusFilter !== "all" || quoteTypeFilter) && (_jsx("button", { onClick: () => handleFilterChange("all"), className: styles.filterOption, children: "Clear Filters" })), _jsxs("button", { onClick: () => handleFilterChange("status"), className: `${styles.filterOption} ${statusFilter !== "all" ? styles.activeFilter : ""}`, children: ["Filter by Status", statusFilter !== "all" && (_jsxs("span", { className: styles.filterValue, children: [":", " ", STATUS_LABELS[statusFilter] ?? statusFilter] }))] }), _jsxs("button", { onClick: () => handleFilterChange("quoteType"), className: `${styles.filterOption} ${quoteTypeFilter ? styles.activeFilter : ""}`, children: ["Filter by Quote Type", quoteTypeFilter && (_jsxs("span", { className: styles.filterValue, children: [": ", quoteTypeFilter] }))] })] }))] }), _jsx("button", { className: styles.createBtn, onClick: handleNewEstimation, children: "Add Estimation" })] }) }), loading && (_jsx("div", { className: styles.loading, children: _jsx("p", { children: "Loading estimations..." }) })), error && (_jsxs("div", { className: styles.error, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchQuotes, className: styles.retryBtn, children: "Retry" })] })), !loading && !error && (_jsxs("div", { className: "table-page-listing", children: [_jsx(EstimatingTable, { quotes: quotes, onEditQuote: handleEditQuote, onViewQuote: handleViewQuote, onCreateJob: handleCreateJob, onEditJob: handleEditJob, onDeleteQuote: isAdmin ? handleDeleteQuote : undefined, isDeleting: isDeleting }), _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, pageSize: pageSize, onPageChange: handlePageChange })] })), _jsxs(Modal, { isOpen: isSiteModalOpen, onClose: () => setIsSiteModalOpen(false), children: [_jsxs("div", { className: styles.modalHeader, children: [_jsxs("svg", { className: styles.warningIcon, width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "12", y1: "8", x2: "12", y2: "12" }), _jsx("line", { x1: "12", y1: "16", x2: "12.01", y2: "16" })] }), _jsx("h2", { className: styles.modalTitle, children: "Are you preparing an estimate for an existing site or a new site?" })] }), _jsxs("div", { className: styles.horizontalLayout, children: [_jsxs("div", { className: styles.siteSelectContainer, children: [_jsx("label", { className: styles.siteSelectLabel, children: "Select Existing Site" }), _jsxs("select", { className: styles.siteSelect, value: selectedSiteId, onChange: (e) => {
                                            const siteId = e.target.value;
                                            setSelectedSiteId(siteId);
                                            if (siteId) {
                                                handleSiteSelect(siteId);
                                            }
                                        }, disabled: sitesLoading, children: [_jsx("option", { value: "", children: sitesLoading ? "Loading sites..." : "Select Site" }), availableSites.map((site) => (_jsxs("option", { value: site.id, children: [site.siteName, formatSiteAddress(site) !== "—" &&
                                                        ` - ${formatSiteAddress(site)}`, site.companyName && ` (${site.companyName})`] }, site.id)))] })] }), _jsx("div", { className: styles.orDivider, children: "OR" }), _jsx("button", { className: styles.createBtn, onClick: handleAddNewEstimate, children: "Add New Estimate" })] })] }), _jsx(EstimatingFilterModal, { isOpen: filterModalType !== null, onClose: () => setFilterModalType(null), filterType: filterModalType === null ? "status" : filterModalType, currentValue: (filterModalType === "status"
                    ? statusFilter !== "all"
                        ? statusFilter
                        : ""
                    : filterModalType === "quoteType"
                        ? (quoteTypeFilter ?? "")
                        : "") ?? "", onApply: handleFilterApply })] }));
};
